import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const HowToBringTheCuripodBuzzToYourSchool = () => {
  const post = getBlogPost('how-to-bring-the-curipod-buzz-to-your-school');
  return (
    <BlogArticle post={post}>
      <p>{'Curipod is transforming classrooms with one simple idea:'}</p>
      <p>
        <strong>{'Make the best thing to do the easiest thing to do.'}</strong>
      </p>
      <p>
        {
          'And the best thing to do? Embed research-based practices—like real-time feedback, student voice, and active participation—into fast, intuitive, and ready-to-use tools. With Curipod, great teaching becomes the easy choice, every time.'
        }
      </p>
      <p>
        {
          'From 100% student participation to real-time feedback and AI-powered lesson design, Curipod helps teachers deliver engaging, discussion-rich instruction without burning out. And the best part?'
        }
        <br />
      </p>
      <p>
        {'D'}
        <em>{'ozens'}</em>
        {
          ' of funding sources can help your school or district bring Curipod to more teachers and students. Whether you’re a school leader exploring new tech, an instructional coach supporting teachers, or a district administrator seeking smart ways to use federal or state funds, this guide is for you.'
        }
      </p>
      <p>
        {
          'We’ve outlined the most relevant funding sources for K–12 schools, how Curipod aligns with their requirements, and how to get started.'
        }
      </p>
      <h2>{'Where the Funds Are: Federal and State Sources to Explore'}</h2>
      <p>
        {
          'To help schools navigate funding processes, we put together a practical guide on key federal funding sources you can use to cover Curipod licenses, PD, and tech integration, so that districts can feel confident '
        }
        <strong>{'making the best thing to do the easiest thing to do'}</strong>
        {'.'}
      </p>
      <p>{'We’ve included:'}</p>
      <ul>
        <li>{'A brief description of each funding stream'}</li>
        <li>{'How Curipod qualifies under its requirements'}</li>
        <li>{'Notes to guide your planning and next steps'}</li>
      </ul>
      <figure>
        <img
          src="/blog/how-to-bring-the-curipod-buzz-to-your-school/inline-1.jpg"
          alt={
            'Table of school funding sources, including Title I through Title V, IDEA, Perkins V, and ESSER, and how each can support Curipod.'
          }
        />
      </figure>
      <h2>{'State Spotlights'}</h2>
      <p>
        <strong>{'Texas'}</strong>
      </p>
      <ul>
        <li>
          {'Title IV, Part A funds support '}
          <strong>{'effective use of tech'}</strong>
          {' for digital literacy and student engagement: '}
          <a
            href={
              'https://tea.texas.gov/finance-and-grants/grants/essa-program/title-iv-part-a-student-support-and-academic-enrichment?utm_source=chatgpt.com'
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {'journals.sagepub.com+13tea.texas.gov+13tea4avcastro.tea.state.tx.us+13'}
          </a>
          {'.'}
        </li>
        <li>
          {'Title I, II, & III grants often use '}
          <strong>{'REAP Flex funding'}</strong>
          {', allowing creative allocation for Curipod integration.'}
        </li>
      </ul>
      <p>
        <strong>{'California'}</strong>
      </p>
      <ul>
        <li>
          {'Title I schools can leverage '}
          <strong>{'Tier 3–4 evidence funding'}</strong>
          {' for Curipod under schoolwide programs'}
          <a
            href={
              'https://www.csba.org/-/media/CSBA/Files/Advocacy/LegislativeAdvocacy/201805_2018ESSASummary.ashx?la=en&rev=5df22caa7bb140eb883962424661c31b&utm_source=chatgpt.com'
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {'ed.gov+15csba.org+15tea4avcastro.tea.state.tx.us+15'}
          </a>
          <a
            href={'https://www.cde.ca.gov/re/es/evidence.asp?utm_source=chatgpt.com'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {'journals.sagepub.com+2cde.ca.gov+2cde.ca.gov+2'}
          </a>
          {'.'}
        </li>
        <li>
          {'Local Control and Accountability Plans (LCAPs) allow districts to include '}
          <strong>{'Curipod PD in Title I'}</strong>
          {' addendums.'}
        </li>
      </ul>
      <h2>{'Real Classroom Use Cases'}</h2>
      <p>
        {
          "Curipod isn’t just tech—it’s a teaching partner. Here's how schools across the country are already using it with success:"
        }
      </p>
      <ul>
        <li>
          <strong>{'Title I'}</strong>
          {': Boosting literacy outcomes and engagement in high-poverty schools'}
        </li>
        <li>
          <strong>{'Title II'}</strong>
          {': Supporting teacher training through PD sessions and Curi-Lunches'}
        </li>
        <li>
          <strong>{'Title III'}</strong>
          {': Empowering English Learners with translation tools and '}
        </li>
        <li>
          <strong>{'IDEA'}</strong>
          {': Meeting IEP goals through personalized, interactive content'}
        </li>
        <li>
          <strong>{'Perkins V'}</strong>
          {': Bringing student voice and creativity into CTE programs'}
        </li>
      </ul>
      <p>
        {
          "Whether you're focused on closing learning gaps, enhancing digital literacy, or supporting multilingual learners, Curipod fits."
        }
      </p>
      <h2>{'Tips for Success'}</h2>
      <ol>
        <li>
          <strong>{'Tie it to evidence-based goals'}</strong>
          {' – Cite case studies and achievement data with every application.'}
        </li>
        <li>
          <strong>{'Bundle PD + licensing smartly'}</strong>
          {' – Use Title II for training; Title I/IV for tech costs.'}
        </li>
        <li>
          <strong>{'Mention UDL and literacy'}</strong>
          {' – Title III and literacy grants love accessibility and language features.'}
        </li>
        <li>
          <strong>{'Use REAP flexibility for small/rural districts'}</strong>
          {' – Funds from II/IV can support Curipod adoption.'}
        </li>
        <li>
          <strong>{'Offer easy-to-use proposal language'}</strong>
          {' – See the sidebar below for ready-to-go wording.'}
        </li>
      </ol>
      <p>
        <strong>{'Sample Proposal Language:'}</strong>
      </p>
      <p>
        {'“We propose to use '}
        <strong>
          <em>{'Title IV, Part A'}</em>
        </strong>
        {
          ' funds to purchase Curipod—a research-backed, AI-driven platform that fuels whole-class engagement, student voice, real-time feedback, and digital literacy for all students.”'
        }
      </p>
      <h2>{'A Final Word'}</h2>
      <p>
        {'Curipod supports '}
        <strong>{'big-picture instructional goals'}</strong>
        {'—from closing gaps to boosting critical thinking—while making the '}
        <strong>{'best teaching moves easy to enact'}</strong>
        {
          '. As funding year deadlines (like Title IV and CA LCAP) approach, these are powerful opportunities to bring the '
        }
        <strong>{'Curipod buzz'}</strong>
        {' into your school.'}
      </p>
      <p>
        {'Reach out if you’d like help mapping your funding pathways! '}
        <Link href="/" passHref>
          <a>{'https://curipod.com/'}</a>
        </Link>
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default HowToBringTheCuripodBuzzToYourSchool;
